import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { CheckCircle2, XCircle, ScanLine } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";

interface ExtractedData {
  insurance_type?: string;
  member_name?: string;
  member_id?: string;
  medicare_id?: string;
  medicaid_id?: string;
  group_id?: string;
  plan_type?: string;
  payer_name?: string;
  claims_address?: string;
  payer_phone?: string;
  edi_payer_id?: string;
  rx_bin?: string;
  rx_pcn?: string;
  rx_group?: string;
  rx_id?: string;
  effective_date?: string;
  copay_amount?: string;
  deductible_info?: string;
  network_tier?: string;
  medicare_part_a?: boolean;
  medicare_part_b?: boolean;
  medicare_part_c?: boolean;
  medicare_part_d?: boolean;
}

interface ScanConfirmDialogProps {
  open: boolean;
  data: ExtractedData | null;
  onConfirm: () => void;
  onCancel: () => void;
}

const INSURANCE_TYPE_LABELS: Record<string, string> = {
  medicare: "Medicare",
  medicaid: "Medicaid",
  dual_eligible: "Dual Eligible (Medicare + Medicaid)",
  commercial: "Commercial",
};

export function ScanConfirmDialog({ open, data, onConfirm, onCancel }: ScanConfirmDialogProps) {
  if (!data) return null;

  const identificationFields: { label: string; value: string | undefined | null }[] = [
    { label: "Insurance Type", value: data.insurance_type ? INSURANCE_TYPE_LABELS[data.insurance_type] || data.insurance_type : undefined },
    { label: "Member Name", value: data.member_name as string },
    { label: "Member ID", value: data.member_id as string },
    { label: "Medicare ID", value: data.medicare_id as string },
    { label: "Medicaid ID", value: data.medicaid_id as string },
    { label: "Group ID", value: data.group_id as string },
    { label: "Plan Type", value: data.plan_type as string },
    { label: "Effective Date", value: data.effective_date as string },
  ];

  const payerFields: { label: string; value: string | undefined | null }[] = [
    { label: "Payer Name", value: data.payer_name as string },
    { label: "Claims Address", value: data.claims_address as string },
    { label: "Payer Phone", value: data.payer_phone as string },
    { label: "EDI Payer ID", value: data.edi_payer_id as string },
  ];

  const pharmacyFields: { label: string; value: string | undefined | null }[] = [
    { label: "Rx BIN", value: data.rx_bin as string },
    { label: "Rx PCN", value: data.rx_pcn as string },
    { label: "Rx Group", value: data.rx_group as string },
    { label: "Rx ID", value: data.rx_id as string },
  ];

  const additionalFields: { label: string; value: string | undefined | null }[] = [
    { label: "Copay", value: data.copay_amount as string },
    { label: "Deductible", value: data.deductible_info as string },
    { label: "Network Tier", value: data.network_tier as string },
  ];

  const parts = [
    { label: "Part A (Hospital)", value: data.medicare_part_a },
    { label: "Part B (Medical)", value: data.medicare_part_b },
    { label: "Part C (Advantage)", value: data.medicare_part_c },
    { label: "Part D (Prescription)", value: data.medicare_part_d },
  ];

  const hasAnyPart = parts.some((p) => p.value);
  const hasPayerFields = payerFields.some((f) => f.value);
  const hasPharmacyFields = pharmacyFields.some((f) => f.value);
  const hasAdditionalFields = additionalFields.some((f) => f.value);

  const FieldSection = ({ title, fields }: { title: string; fields: { label: string; value: string | undefined | null }[] }) => {
    const activeFields = fields.filter((f) => f.value);
    if (activeFields.length === 0) return null;
    return (
      <div className="pt-2 border-t">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{title}</span>
        <div className="mt-1 space-y-1.5">
          {activeFields.map((f) => (
            <div key={f.label} className="flex justify-between items-center text-sm">
              <span className="text-muted-foreground">{f.label}</span>
              <span className="font-medium text-foreground text-right max-w-[60%] break-words">{f.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const allEmpty = !identificationFields.some((f) => f.value) && !hasAnyPart && !hasPayerFields && !hasPharmacyFields && !hasAdditionalFields;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ScanLine className="h-5 w-5 text-primary" />
            Insurance Card Scan Results
          </DialogTitle>
          <DialogDescription>
            Review the extracted data below. Click "Apply" to populate the form or "Discard" to enter manually.
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh]">
          <div className="space-y-3 py-2 pr-4">
            <FieldSection title="Identification" fields={identificationFields} />

            {hasAnyPart && (
              <div className="pt-2 border-t">
                <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Medicare Coverage</span>
                <div className="flex flex-wrap gap-2 mt-1">
                  {parts.map((p) => (
                    <Badge key={p.label} variant={p.value ? "default" : "outline"} className={p.value ? "" : "opacity-50"}>
                      {p.value ? <CheckCircle2 className="h-3 w-3 mr-1" /> : <XCircle className="h-3 w-3 mr-1" />}
                      {p.label}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            <FieldSection title="Payer Information" fields={payerFields} />
            <FieldSection title="Pharmacy Benefits" fields={pharmacyFields} />
            <FieldSection title="Additional Details" fields={additionalFields} />

            {allEmpty && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No data could be extracted. Please enter details manually.
              </p>
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button type="button" variant="outline" onClick={onCancel}>
            Discard
          </Button>
          <Button type="button" onClick={onConfirm}>
            Apply to Form
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
